import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash-es/debounce';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BlockNoteEditorComponent } from '@/components/atoms/Editors/BlockNoteEditor/BlockNoteEditor';
import { getGuidelinesEditorOverrides } from '@/components/atoms/Editors/BlockNoteEditor/config';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import { AuthConfig, AuthStep } from '@/shared/types/node';

import { AuthStepKey } from '../types';

const CLOSE_ANIMATION_MS = 300;

interface AuthPageGuidelinesPanelProps {
  step: AuthStepKey;
  authConfig: AuthConfig;
  onUpdate: (updates: Partial<AuthConfig>) => void;
  onClose: () => void;
}

const AuthPageGuidelinesPanel = ({
  step,
  authConfig,
  onUpdate,
  onClose,
}: AuthPageGuidelinesPanelProps) => {
  const { t } = useTranslation();
  const dictionary = getGuidelinesEditorOverrides({
    defaultPlaceholder: t('dialog.auth.guidelines_editor_placeholder'),
  }).dictionary;
  const { contextVariables } = useBrainVariables();

  const stepFieldKey = `${step}_step` as const;
  const stepConfig: AuthStep | undefined = authConfig[stepFieldKey];

  const [isOpen, setIsOpen] = useState(true);
  const getMarkdownRef = useRef<() => Promise<string>>(null);
  const closingRef = useRef(false);

  const persistGuideline = useMemo(
    () =>
      debounce(async () => {
        const guideline = (await getMarkdownRef.current?.()) ?? stepConfig?.guideline ?? '';
        onUpdate({ [stepFieldKey]: { ...stepConfig, guideline } } as Partial<AuthConfig>);
      }, 500),
    [onUpdate, stepConfig, stepFieldKey]
  );

  useEffect(() => () => persistGuideline.cancel(), [persistGuideline]);

  const handleEditorReady = useCallback(
    (editorApi: {
      appendContent: (markdown: string) => void;
      getMarkdown: () => Promise<string>;
    }) => {
      getMarkdownRef.current = editorApi.getMarkdown;
    },
    []
  );

  const handleChange = useCallback(() => {
    persistGuideline();
  }, [persistGuideline]);

  const handleClose = useCallback(() => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    persistGuideline.flush();
    setIsOpen(false);
    setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  }, [onClose, persistGuideline]);

  return (
    <Drawer
      direction="right"
      open={isOpen}
      onOpenChange={() => {}}
      modal={false}
      shouldScaleBackground={false}
      dismissible={false}
    >
      <DrawerContent className="top-[calc(var(--newheader-height)+var(--subheader-height))] bottom-0 h-auto w-[768px] border-l shadow-lg">
        <div className="relative">
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('common.close')}
            onClick={handleClose}
            className="absolute top-4 right-4 z-10"
          >
            <XIcon size={16} />
          </Button>
        </div>

        <DrawerHeader className="gap-0 pt-11 pr-12 pb-0 pl-24">
          <DrawerTitle className="text-lg font-semibold text-foreground">
            {t('dialog.auth.guidelines_editor_title')}
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm text-muted-foreground">
            {t('dialog.auth.guidelines_editor_description')}
          </DrawerDescription>
        </DrawerHeader>

        <Separator className="mt-4 mr-6 ml-24 w-auto" />

        <div className="flex-1 overflow-y-auto pt-4 pr-6 pb-6 pl-24" data-vaul-no-drag>
          <BlockNoteEditorComponent
            initialMarkdown={stepConfig?.guideline ?? ''}
            dictionary={dictionary}
            contextVariableOptions={contextVariables}
            onEditorReady={handleEditorReady}
            onChange={handleChange}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AuthPageGuidelinesPanel;
